<script setup lang="ts">
import { EmptyState, TraceIdAlert, formatDateTime } from '@yc/ui-kit'
import { computed, onMounted, reactive, ref } from 'vue'

import { MODES, MODE_LABELS, listCarrierOptions, type Carrier, type TransportMode } from '../api/carriers'
import { listHubOptions, type Hub } from '../api/hubs'
import {
  SEG_STATUS_LABELS,
  SEG_STATUS_TAG,
  STATUS_LABELS,
  STATUS_TAG,
  canAct,
  canDoneSegment,
  cancelPlan,
  confirmPlan,
  createPlan,
  doneSegment,
  getPlan,
  listPlans,
  planInput,
  replaceSegments,
  segmentInputs,
  updatePlan,
  type PlanDetail,
  type PlanRow,
  type PlanStatus,
  type Segment,
} from '../api/plans'

const rows = ref<PlanRow[]>([])
const total = ref(0)
const loading = ref(false)
const error = ref<unknown>(null)
const refError = ref<unknown>(null)
const notice = ref('')

const carriers = ref<Carrier[]>([])
const hubs = ref<Hub[]>([])

const query = reactive({ keyword: '', status: '', page: 1, size: 20 })

const formOpen = ref(false)
const editing = ref<PlanRow | PlanDetail | null>(null)
const saving = ref(false)
const formError = ref<unknown>(null)

/** 区段编辑行：id 未选时是 undefined、时间没填时是 null，提交时都不进 body */
interface SegmentRow {
  mode: TransportMode | ''
  carrier_id?: number
  from_hub_id?: number
  to_hub_id?: number
  depart_at: string | null
  arrive_at: string | null
  distance_km: number
  cost: number
}

const form = reactive({
  cargo_desc: '',
  weight_kg: 0,
  origin: '',
  dest: '',
  remark: '',
  segments: [] as SegmentRow[],
})

const detailOpen = ref(false)
const detail = ref<PlanDetail | null>(null)
const detailLoading = ref(false)
const detailError = ref<unknown>(null)
/** 状态流转与逐段完成的失败（非法流转是 409），与加载失败分开显示，都带 trace_id */
const actionError = ref<unknown>(null)

const originError = computed(() => (form.origin.trim() ? '' : '请填写起运地'))
const destError = computed(() => (form.dest.trim() ? '' : '请填写目的地'))

/** 提交前的整体校验：与后端 internal/plan/service.go 的 validateInput / buildSegment 一一对应 */
const formErrorText = computed(() => {
  if (originError.value) return originError.value
  if (destError.value) return destError.value
  if (Number(form.weight_kg) < 0) return '重量不能为负数'
  if (!form.segments.length) return '请至少添加 1 个区段'
  for (const [i, seg] of form.segments.entries()) {
    const n = i + 1
    if (!seg.mode) return `第 ${n} 段请选择运输方式`
    if (!seg.carrier_id) return `第 ${n} 段请选择承运商`
    if (!seg.from_hub_id) return `第 ${n} 段请选择起运场站`
    if (!seg.to_hub_id) return `第 ${n} 段请选择目的场站`
    if (Number(seg.distance_km) < 0) return `第 ${n} 段里程不能为负数`
    if (Number(seg.cost) < 0) return `第 ${n} 段费用不能为负数`
    if (seg.depart_at && seg.arrive_at && seg.arrive_at < seg.depart_at) {
      return `第 ${n} 段到达时间不能早于出发时间`
    }
  }
  return ''
})

async function load(noticeText = ''): Promise<void> {
  loading.value = true
  error.value = null
  try {
    const res = await listPlans({
      page: query.page,
      size: query.size,
      keyword: query.keyword.trim() || undefined,
      status: query.status || undefined,
    })
    rows.value = res.list
    total.value = res.total
    notice.value = noticeText
  } catch (e) {
    error.value = e
    rows.value = []
  } finally {
    loading.value = false
  }
}

async function loadRefs(): Promise<void> {
  refError.value = null
  try {
    const [c, h] = await Promise.all([listCarrierOptions(), listHubOptions()])
    carriers.value = c
    hubs.value = h
  } catch (e) {
    // 参照数据拿不到下拉会空，但必须把原因露出来，否则页面看着像没数据
    refError.value = e
  }
}

onMounted(() => {
  void load()
  void loadRefs()
})

function search(): void {
  query.page = 1
  void load()
}

function page(p: number): void {
  query.page = p
  void load()
}

function emptySegment(): SegmentRow {
  return {
    mode: 'truck',
    carrier_id: undefined,
    from_hub_id: undefined,
    to_hub_id: undefined,
    depart_at: null,
    arrive_at: null,
    distance_km: 0,
    cost: 0,
  }
}

function addSegment(): void {
  form.segments.push(emptySegment())
}

function removeSegment(index: number): void {
  form.segments.splice(index, 1)
}

function resetForm(): void {
  form.cargo_desc = ''
  form.weight_kg = 0
  form.origin = ''
  form.dest = ''
  form.remark = ''
  form.segments = [emptySegment()]
}

function openCreate(): void {
  detailOpen.value = false
  editing.value = null
  resetForm()
  formError.value = null
  formOpen.value = true
}

/** 后端回的是 RFC3339，date-picker 的 value-format 是 YYYY-MM-DD HH:mm:ss，截到秒即可 */
function toLocal(iso: string | null): string | null {
  if (!iso) return null
  return `${iso.slice(0, 10)} ${iso.slice(11, 19)}`
}

async function openEdit(row: PlanRow | PlanDetail): Promise<void> {
  detailOpen.value = false
  formError.value = null
  editing.value = row
  try {
    const d = await getPlan(row.id)
    form.cargo_desc = d.cargo_desc
    form.weight_kg = d.weight_kg
    form.origin = d.origin
    form.dest = d.dest
    form.remark = d.remark
    form.segments = d.segments.map((seg) => ({
      mode: seg.mode,
      carrier_id: seg.carrier_id || undefined,
      from_hub_id: seg.from_hub_id || undefined,
      to_hub_id: seg.to_hub_id || undefined,
      depart_at: toLocal(seg.depart_at),
      arrive_at: toLocal(seg.arrive_at),
      distance_km: seg.distance_km,
      cost: seg.cost,
    }))
    if (!form.segments.length) form.segments = [emptySegment()]
    formOpen.value = true
  } catch (e) {
    refError.value = e
  }
}

async function save(): Promise<void> {
  if (formErrorText.value) return
  saving.value = true
  formError.value = null
  const input = planInput(form)
  try {
    // 两步走：先计划头，再整体替换区段（服务端按区段 SUM 重算 est_cost / total_distance_km）。
    // 编辑时计划头先落库、区段失败的话错误原样抛出，计划仍是 draft 可重试。
    const plan = editing.value ? await updatePlan(editing.value.id, input) : await createPlan(input)
    const resp = await replaceSegments(plan.id, segmentInputs(form.segments))
    formOpen.value = false
    await load(editing.value ? `计划 ${resp.doc_no} 已更新` : `计划 ${resp.doc_no} 已创建`)
  } catch (e) {
    formError.value = e
  } finally {
    saving.value = false
  }
}

async function openDetail(row: PlanRow): Promise<void> {
  detail.value = null
  detailError.value = null
  actionError.value = null
  detailLoading.value = true
  detailOpen.value = true
  try {
    detail.value = await getPlan(row.id)
  } catch (e) {
    detailError.value = e
  } finally {
    detailLoading.value = false
  }
}

async function act(kind: 'confirm' | 'cancel'): Promise<void> {
  if (!detail.value) return
  actionError.value = null
  const { id, doc_no: docNo } = detail.value
  try {
    const resp = kind === 'confirm' ? await confirmPlan(id) : await cancelPlan(id)
    detail.value = resp
    await load(`计划 ${docNo} 已${kind === 'confirm' ? '确认' : '取消'}`)
  } catch (e) {
    // 非法流转（如重复确认）后端 409，把 trace_id 露出来
    actionError.value = e
  }
}

async function markDone(seg: Segment): Promise<void> {
  if (!detail.value) return
  actionError.value = null
  const { id, doc_no: docNo } = detail.value
  try {
    const resp = await doneSegment(id, seg.seq)
    detail.value = resp
    await load(`计划 ${docNo} 第 ${seg.seq} 段已完成`)
  } catch (e) {
    actionError.value = e
  }
}

function carrierLabel(id: number): string {
  const c = carriers.value.find((item) => item.id === id)
  return c ? c.name || c.code : `#${id}`
}

function hubLabel(id: number): string {
  const h = hubs.value.find((item) => item.id === id)
  return h ? h.name || h.code : `#${id}`
}

/** 区段完成比例：列表进度与详情都用它 */
function donePercent(row: PlanRow): number {
  if (!row.segment_count) return 0
  return Math.round((row.done_count * 100) / row.segment_count)
}
</script>

<template>
  <div class="yc-plan">
    <header class="yc-plan__bar">
      <el-input
        v-model="query.keyword"
        class="yc-plan__keyword"
        placeholder="单号 / 货描 / 起讫地"
        clearable
        @keyup.enter="search"
        @clear="search"
      />
      <el-select v-model="query.status" class="yc-plan__status" placeholder="全部状态" clearable @change="search">
        <el-option v-for="(label, value) in STATUS_LABELS" :key="value" :label="label" :value="value" />
      </el-select>
      <el-button @click="search">查询</el-button>
      <el-button v-perm="'mtm:plan:create'" type="primary" @click="openCreate">新建计划</el-button>
      <el-button @click="load()">刷新</el-button>
      <span v-if="notice" class="yc-plan__notice">{{ notice }}</span>
    </header>

    <div v-if="error || refError" class="yc-plan__error">
      <TraceIdAlert :error="error ?? refError" />
    </div>

    <el-card v-loading="loading" class="yc-plan__card">
      <el-table v-if="rows.length" :data="rows" row-key="id">
        <el-table-column prop="doc_no" label="计划单号" min-width="150" />
        <el-table-column prop="cargo_desc" label="货描" min-width="150" show-overflow-tooltip>
          <template #default="{ row }">{{ row.cargo_desc || '—' }}</template>
        </el-table-column>
        <el-table-column label="起讫地" min-width="170" show-overflow-tooltip>
          <template #default="{ row }">{{ row.origin }} → {{ row.dest }}</template>
        </el-table-column>
        <el-table-column label="状态" width="100">
          <template #default="{ row }">
            <el-tag :type="STATUS_TAG[row.status as PlanStatus] ?? 'info'" size="small">
              {{ STATUS_LABELS[row.status as PlanStatus] ?? row.status }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="区段进度" width="130">
          <template #default="{ row }">
            <span class="yc-plan__segtext">{{ row.done_count }}/{{ row.segment_count }} 段</span>
            <el-progress
              :percentage="donePercent(row)"
              :stroke-width="6"
              :show-text="false"
              class="yc-plan__progress"
            />
          </template>
        </el-table-column>
        <el-table-column label="估算里程（km）" width="130" align="right">
          <template #default="{ row }">{{ Number(row.total_distance_km).toFixed(2) }}</template>
        </el-table-column>
        <el-table-column label="预计成本（元）" width="130" align="right">
          <template #default="{ row }">{{ Number(row.est_cost).toFixed(2) }}</template>
        </el-table-column>
        <el-table-column label="更新时间" min-width="155">
          <template #default="{ row }">{{ formatDateTime(row.updated_at) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="140" fixed="right">
          <template #default="{ row }">
            <el-button link type="primary" @click="openDetail(row)">详情</el-button>
            <el-button
              v-if="canAct(row.status, 'edit')"
              v-perm="'mtm:plan:update'"
              link
              type="primary"
              @click="openEdit(row)"
            >
              编辑
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <EmptyState
        v-else-if="!loading"
        title="还没有联运计划"
        description="先建计划头，再编排区段，确认后即可逐段完成。"
      >
        <el-button v-perm="'mtm:plan:create'" type="primary" @click="openCreate">新建计划</el-button>
      </EmptyState>
    </el-card>

    <el-pagination
      v-if="total > query.size"
      class="yc-plan__page"
      layout="total, prev, pager, next"
      :total="total"
      :page-size="query.size"
      :current-page="query.page"
      @current-change="page"
    />

    <!-- 新建 / 编辑：计划头 + 区段编辑器 -->
    <el-dialog
      v-if="formOpen"
      v-model="formOpen"
      :title="editing ? `编辑计划 · ${editing.doc_no}` : '新建联运计划'"
      width="920px"
      :close-on-click-modal="false"
    >
      <div v-if="formError" class="yc-plan__error">
        <TraceIdAlert :error="formError" />
      </div>

      <el-form label-position="top" @submit.prevent="save">
        <div class="yc-plan__formrow">
          <el-form-item label="货描">
            <el-input v-model="form.cargo_desc" class="yc-plan__field" maxlength="255" placeholder="如 日用百货 6 托，可留空" />
          </el-form-item>
          <el-form-item label="重量（kg）">
            <el-input-number
              v-model="form.weight_kg"
              class="yc-plan__num"
              :min="0"
              :max="999999999"
              :precision="3"
              controls-position="right"
            />
          </el-form-item>
          <el-form-item label="起运地" :error="originError">
            <el-input v-model="form.origin" class="yc-plan__field" maxlength="255" placeholder="如 上海" />
          </el-form-item>
          <el-form-item label="目的地" :error="destError">
            <el-input v-model="form.dest" class="yc-plan__field" maxlength="255" placeholder="如 乌鲁木齐" />
          </el-form-item>
          <el-form-item label="备注">
            <el-input v-model="form.remark" class="yc-plan__field" maxlength="255" placeholder="可留空" />
          </el-form-item>
        </div>

        <el-form-item label="运输区段">
          <div class="yc-plan__segs">
            <div v-for="(seg, index) in form.segments" :key="index" class="yc-plan__seg">
              <div class="yc-plan__seghead">
                <el-tag size="small">第 {{ index + 1 }} 段</el-tag>
                <span class="yc-plan__hint">序号提交时按顺序自动 {{ index + 1 }}</span>
                <el-button link type="danger" @click="removeSegment(index)">删除</el-button>
              </div>

              <div class="yc-plan__seggrid">
                <div class="yc-plan__cell">
                  <span class="yc-plan__label">运输方式</span>
                  <el-select v-model="seg.mode" placeholder="选择方式">
                    <el-option v-for="m in MODES" :key="m" :label="MODE_LABELS[m]" :value="m" />
                  </el-select>
                </div>
                <div class="yc-plan__cell">
                  <span class="yc-plan__label">承运商</span>
                  <el-select v-model="seg.carrier_id" filterable placeholder="选择承运商">
                    <el-option
                      v-for="c in carriers"
                      :key="c.id"
                      :label="`${c.code} ${c.name}`.trim()"
                      :value="c.id"
                    />
                  </el-select>
                </div>
                <div class="yc-plan__cell">
                  <span class="yc-plan__label">起运场站</span>
                  <el-select v-model="seg.from_hub_id" filterable placeholder="选择起运场站">
                    <el-option
                      v-for="h in hubs"
                      :key="h.id"
                      :label="`${h.code} ${h.name}`.trim()"
                      :value="h.id"
                    />
                  </el-select>
                </div>
                <div class="yc-plan__cell">
                  <span class="yc-plan__label">目的场站</span>
                  <el-select v-model="seg.to_hub_id" filterable placeholder="选择目的场站">
                    <el-option
                      v-for="h in hubs"
                      :key="h.id"
                      :label="`${h.code} ${h.name}`.trim()"
                      :value="h.id"
                    />
                  </el-select>
                </div>
                <div class="yc-plan__cell">
                  <span class="yc-plan__label">出发时间</span>
                  <el-date-picker
                    v-model="seg.depart_at"
                    type="datetime"
                    value-format="YYYY-MM-DD HH:mm:ss"
                    placeholder="可留空"
                  />
                </div>
                <div class="yc-plan__cell">
                  <span class="yc-plan__label">到达时间</span>
                  <el-date-picker
                    v-model="seg.arrive_at"
                    type="datetime"
                    value-format="YYYY-MM-DD HH:mm:ss"
                    placeholder="可留空"
                  />
                </div>
                <div class="yc-plan__cell">
                  <span class="yc-plan__label">里程（km）</span>
                  <el-input-number
                    v-model="seg.distance_km"
                    :min="0"
                    :max="999999"
                    :precision="2"
                    controls-position="right"
                  />
                </div>
                <div class="yc-plan__cell">
                  <span class="yc-plan__label">成本（元）</span>
                  <el-input-number
                    v-model="seg.cost"
                    :min="0"
                    :max="999999999"
                    :precision="2"
                    controls-position="right"
                  />
                </div>
              </div>
            </div>

            <div class="yc-plan__segadd">
              <el-button @click="addSegment">添加区段</el-button>
              <span class="yc-plan__hint">
                至少 1 个区段；里程与预计成本由服务端按区段汇总，只读展示。
              </span>
            </div>
          </div>
        </el-form-item>

        <el-button type="primary" native-type="submit" :loading="saving" :disabled="!!formErrorText">
          {{ editing ? '保存' : '创建' }}
        </el-button>
        <span v-if="formErrorText" class="yc-plan__hint yc-plan__hint--inline">{{ formErrorText }}</span>
      </el-form>

      <template #footer>
        <el-button @click="formOpen = false">取消</el-button>
      </template>
    </el-dialog>

    <!-- 详情：计划头 + 区段表格 + 状态机动作 -->
    <el-dialog
      v-if="detailOpen"
      v-model="detailOpen"
      :title="detail ? `计划详情 · ${detail.doc_no}` : '计划详情'"
      width="1020px"
      :close-on-click-modal="false"
    >
      <div v-if="detailError || actionError" class="yc-plan__error">
        <TraceIdAlert :error="detailError ?? actionError" />
      </div>

      <div v-loading="detailLoading">
        <template v-if="detail">
          <div class="yc-plan__facts">
            <div class="yc-plan__fact">
              <span>状态</span>
              <el-tag :type="STATUS_TAG[detail.status]" size="small">{{ STATUS_LABELS[detail.status] }}</el-tag>
            </div>
            <div class="yc-plan__fact"><span>货描</span>{{ detail.cargo_desc || '—' }}</div>
            <div class="yc-plan__fact"><span>重量</span>{{ Number(detail.weight_kg).toFixed(3) }} kg</div>
            <div class="yc-plan__fact"><span>起讫地</span>{{ detail.origin }} → {{ detail.dest }}</div>
            <div class="yc-plan__fact">
              <span>估算里程</span>{{ Number(detail.total_distance_km).toFixed(2) }} km（由区段汇总）
            </div>
            <div class="yc-plan__fact">
              <span>预计成本</span>{{ Number(detail.est_cost).toFixed(2) }} 元（由区段汇总）
            </div>
            <div class="yc-plan__fact"><span>备注</span>{{ detail.remark || '—' }}</div>
            <div class="yc-plan__fact"><span>创建时间</span>{{ formatDateTime(detail.created_at) }}</div>
            <div class="yc-plan__fact"><span>更新时间</span>{{ formatDateTime(detail.updated_at) }}</div>
          </div>

          <h3 class="yc-plan__sect">运输区段（{{ detail.segments.length }}）</h3>
          <el-table v-if="detail.segments.length" :data="detail.segments" row-key="seq" size="small">
            <el-table-column prop="seq" label="序号" width="60" align="center" />
            <el-table-column label="方式" width="80">
              <template #default="{ row }">{{ MODE_LABELS[row.mode as TransportMode] ?? row.mode }}</template>
            </el-table-column>
            <el-table-column label="承运商" min-width="130" show-overflow-tooltip>
              <template #default="{ row }">{{ carrierLabel(row.carrier_id) }}</template>
            </el-table-column>
            <el-table-column label="起运场站" min-width="130" show-overflow-tooltip>
              <template #default="{ row }">{{ hubLabel(row.from_hub_id) }}</template>
            </el-table-column>
            <el-table-column label="目的场站" min-width="130" show-overflow-tooltip>
              <template #default="{ row }">{{ hubLabel(row.to_hub_id) }}</template>
            </el-table-column>
            <el-table-column label="出发时间" min-width="150">
              <template #default="{ row }">{{ formatDateTime(row.depart_at) }}</template>
            </el-table-column>
            <el-table-column label="到达时间" min-width="150">
              <template #default="{ row }">{{ formatDateTime(row.arrive_at) }}</template>
            </el-table-column>
            <el-table-column label="里程（km）" width="100" align="right">
              <template #default="{ row }">{{ Number(row.distance_km).toFixed(2) }}</template>
            </el-table-column>
            <el-table-column label="成本（元）" width="100" align="right">
              <template #default="{ row }">{{ Number(row.cost).toFixed(2) }}</template>
            </el-table-column>
            <el-table-column label="状态" width="90">
              <template #default="{ row }">
                <el-tag :type="SEG_STATUS_TAG[row.status as Segment['status']] ?? 'info'" size="small">
                  {{ SEG_STATUS_LABELS[row.status as Segment['status']] ?? row.status }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="90" fixed="right">
              <template #default="{ row }">
                <el-button
                  v-if="canDoneSegment(detail.status) && row.status !== 'done'"
                  v-perm="'mtm:plan:segment-done'"
                  link
                  type="primary"
                  @click="markDone(row)"
                >
                  完成
                </el-button>
              </template>
            </el-table-column>
          </el-table>
          <p v-else class="yc-plan__hint">这张计划还没有区段，编辑后至少补 1 段才能确认。</p>
        </template>
      </div>

      <template #footer>
        <el-button
          v-if="detail && canAct(detail.status, 'edit')"
          v-perm="'mtm:plan:update'"
          @click="openEdit(detail)"
        >
          编辑
        </el-button>
        <el-button
          v-if="detail && canAct(detail.status, 'confirm')"
          v-perm="'mtm:plan:confirm'"
          type="primary"
          @click="act('confirm')"
        >
          确认
        </el-button>
        <el-popconfirm
          v-if="detail && canAct(detail.status, 'cancel')"
          title="确认取消该计划？取消后不可恢复。"
          width="260"
          @confirm="act('cancel')"
        >
          <template #reference>
            <el-button v-perm="'mtm:plan:cancel'" type="danger">取消计划</el-button>
          </template>
        </el-popconfirm>
        <el-button @click="detailOpen = false">关闭</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<style scoped lang="scss">
.yc-plan {
  &__bar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--yc-space-2);
    margin-bottom: var(--yc-space-4);
  }

  &__keyword {
    width: 220px;
  }

  &__status {
    width: 140px;
  }

  &__notice {
    color: var(--yc-text-secondary);
    font-size: var(--yc-font-sm);
  }

  &__error {
    margin-bottom: var(--yc-space-3);
    font-size: var(--yc-font-sm);
  }

  &__page {
    margin-top: var(--yc-space-4);
    justify-content: flex-end;
  }

  &__segtext {
    font-size: var(--yc-font-sm);
  }

  &__progress {
    margin-top: var(--yc-space-1);
  }

  &__formrow {
    display: flex;
    flex-wrap: wrap;
    gap: var(--yc-space-3);

    .el-form-item {
      margin-bottom: var(--yc-space-3);
    }
  }

  &__field {
    width: 200px;
  }

  &__num {
    width: 170px;
  }

  &__segs {
    width: 100%;
  }

  &__seg {
    margin-bottom: var(--yc-space-3);
    padding: var(--yc-space-3);
    border: 1px solid var(--yc-border);
    border-radius: var(--yc-radius, 6px);
  }

  &__seghead {
    display: flex;
    align-items: center;
    gap: var(--yc-space-2);
    margin-bottom: var(--yc-space-2);
  }

  &__seggrid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(210px, 1fr));
    gap: var(--yc-space-2) var(--yc-space-3);
  }

  &__cell {
    display: flex;
    flex-direction: column;
    gap: var(--yc-space-1);

    .el-select,
    .el-date-editor,
    .el-input-number {
      width: 100%;
    }
  }

  &__label {
    color: var(--yc-text-secondary);
    font-size: var(--yc-font-xs);
  }

  &__segadd {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--yc-space-2);
  }

  &__hint {
    color: var(--yc-text-secondary);
    font-size: var(--yc-font-xs);
    line-height: 1.7;

    &--inline {
      margin-left: var(--yc-space-3);
    }
  }

  &__facts {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
    gap: var(--yc-space-2) var(--yc-space-4);
    margin-bottom: var(--yc-space-4);
  }

  &__fact {
    font-size: var(--yc-font);

    span {
      margin-right: var(--yc-space-2);
      color: var(--yc-text-secondary);
      font-size: var(--yc-font-sm);
    }
  }

  &__sect {
    margin: var(--yc-space-4) 0 var(--yc-space-2);
    font-size: var(--yc-font);
    font-weight: 600;
  }
}
</style>
